import axios from 'axios';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FileText, Plus, Edit, Trash2, XCircle, Archive, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { CreerSoumissionComplete } from './CreerSoumissionComplete';
import { BadgeSuiviClient, BlocSuiviClient } from './SuiviClient'; // 📬 Suivi client

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const STATUT_CONFIG = {
  brouillon: { label: 'Brouillon', class: 'bg-gray-100 text-gray-800' },
  soumission_envoyee: { label: 'Envoyée', class: 'bg-blue-100 text-blue-800' },
  envoyee: { label: 'Envoyée', class: 'bg-blue-100 text-blue-800' },
  acceptee: { label: 'Acceptée', class: 'bg-green-100 text-green-800' },
  refusee: { label: 'Refusée', class: 'bg-red-100 text-red-800' },
  morte: { label: 'Morte', class: 'bg-gray-300 text-gray-600' },
};

export function OngletSoumissions({ dossier, onUpdate }) {
  const [showCreer, setShowCreer] = useState(false);
  const [soumissionType, setSoumissionType] = useState('peinture'); // 'peinture' ou 'location'
  const [editSoumission, setEditSoumission] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionConfirm, setActionConfirm] = useState(null); // {type: 'refuser'|'morte'|'accepter'|'accepter_final', soumission}

  // Supprimer une soumission
  const handleDelete = async (soumission) => {
    try {
      const response = await axios.delete(`${API_URL}/api/crm/soumissions/${soumission.id}`);
      if (response.status !== 200) throw new Error('Erreur');
      toast.success(`Soumission ${soumission.numero_soumission} supprimée`);
      setDeleteConfirm(null);
      onUpdate();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Marquer comme refusée (affecte les performances)
  const handleRefuser = async (soumission) => {
    try {
      const response = await axios.put(`${API_URL}/api/crm/soumissions/${soumission.id}`, { ...soumission, statut: 'refusee' });
      if (response.status !== 200) throw new Error('Erreur');
      // Also update the dossier status
      await axios.put(`${API_URL}/api/crm/dossiers/${dossier.id}`, { ...dossier, statut: 'refuse' });
      toast.success('Soumission marquée comme refusée (affecte les performances)');
      setActionConfirm(null);
      onUpdate();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur');
    }
  };

  // Marquer comme morte (n'affecte PAS les stats)
  const handleMorte = async (soumission) => {
    try {
      const response = await axios.put(`${API_URL}/api/crm/soumissions/${soumission.id}`, { ...soumission, statut: 'morte' });
      if (response.status !== 200) throw new Error('Erreur');
      toast.success("Soumission marquée comme morte (n'affecte pas les performances)");
      setActionConfirm(null);
      onUpdate();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur');
    }
  };

  const [signatureDate, setSignatureDate] = useState('');

  // Accepter manuellement avec date de signature
  const handleAccepter = async (soumission) => {
    try {
      const dateSignature = signatureDate || new Date().toISOString();
      // Mettre à jour la soumission
      const response = await axios.put(`${API_URL}/api/crm/soumissions/${soumission.id}`, { ...soumission, statut: 'acceptee', date_signature: dateSignature, signature_obtenue: true });
      if (response.status !== 200) throw new Error('Erreur');
      // Statut dossier : rester en_attente_production si un BT existe déjà
      const btRes = await axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${dossier.id}`);
      const hasBT = (btRes.data?.bons_travail || []).filter(bt => bt.statut !== 'annule').length > 0;
      await axios.put(`${API_URL}/api/crm/dossiers/${dossier.id}`, { 
          ...dossier, 
          signature_obtenue: true, 
          date_signature: dateSignature,
          montant_soumission: soumission.montant_final || soumission.sous_total || 0,
          statut: hasBT ? 'en_attente_production' : 'accepte'
        });
      toast.success(`Soumission marquée comme acceptée`);
      setActionConfirm(null);
      setSignatureDate('');
      onUpdate();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur');
    }
  };

  // Extraire le texte brut du HTML
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Formater le dépôt selon le type
  const formatDepot = (soum) => {
    const type = soum.depot_type || 'pourcentage';
    const valeur = soum.depot_valeur ?? soum.pourcentage_depot ?? 25;
    const montant = soum.montant_depot_calcule || 0;
    
    if (type === 'pourcentage') {
      return `Dépôt (${valeur}%) : ${montant.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
    } else {
      return `Dépôt : ${valeur.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
    }
  };

  const soumissions = dossier.soumissions || [];
  // 🔁 Historique des relances par soumission (chargé une fois par dossier ; silencieux si la page n'est pas accordée)
  const [relancesParSoumission, setRelancesParSoumission] = useState({});
  useEffect(() => {
    let actif = true;
    Promise.all(soumissions.filter((s) => (s.relances || []).length > 0).map((s) =>
      axios.get(`${API_URL}/api/relances-soumissions/soumission/${s.id}`).then((r) => [s.id, r.data.relances || []]).catch(() => [s.id, []])))
      .then((paires) => { if (actif) setRelancesParSoumission(Object.fromEntries(paires)); });
    return () => { actif = false; };
  }, [soumissions]);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Soumissions</CardTitle>
            <div className="flex items-center gap-2">
              <Button onClick={() => { setSoumissionType('peinture'); setShowCreer(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-soumission-peinture-btn">
                <Plus className="w-4 h-4 mr-2" />
                Soumission Peinture
              </Button>
              <Button onClick={() => { setSoumissionType('location'); setShowCreer(true); }} variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="new-contrat-location-btn">
                <Plus className="w-4 h-4 mr-2" />
                Contrat Location
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {soumissions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Aucune soumission pour ce dossier</p>
              <Button onClick={() => setShowCreer(true)}>Créer la première soumission</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {soumissions.map((soum) => {
                const statutConfig = STATUT_CONFIG[soum.statut] || STATUT_CONFIG.brouillon;
                const descriptionText = stripHtml(soum.description_travaux);
                
                return (
                  <Card 
                    key={soum.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer border-l-4"
                    style={{ borderLeftColor: soum.statut === 'morte' ? '#9ca3af' : soum.statut === 'refusee' ? '#dc2626' : soum.statut === 'acceptee' ? '#16a34a' : '#3b82f6' }}
                    onClick={() => setEditSoumission(soum)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {/* Header: Numéro + Badge statut + Badge type */}
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-bold text-lg">{soum.numero_soumission}</p>
                            <Badge className={statutConfig.class}>{statutConfig.label}</Badge>
                            {soum.type_soumission === 'location' && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300">Location</Badge>
                            )}
                            {['envoyee', 'soumission_envoyee', 'acceptee'].includes(soum.statut) && <BadgeSuiviClient suivi={soum.suivi_client} testid={`soumission-${soum.id}-suivi`} />}
                          </div>
                          
                          {/* Date création */}
                          <p className="text-sm text-gray-500">
                            Créée le {new Date(soum.date_creation).toLocaleDateString('fr-CA')}
                          </p>
                          {(soum.suivi_client?.vu || (soum.relances || []).length > 0) && (
                            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                              <BlocSuiviClient suivi={soum.suivi_client} relances={relancesParSoumission[soum.id] || []} testid={`soumission-${soum.id}-bloc-suivi`} />
                            </div>
                          )}
                          
                          {/* Description (texte brut, pas HTML) */}
                          {descriptionText && (
                            <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                              {descriptionText.length > 150 ? descriptionText.substring(0, 150) + '...' : descriptionText}
                            </p>
                          )}
                          
                          {/* Montant et dépôt */}
                          <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3">
                            <div>
                              <p className="text-sm text-gray-600">Montant total</p>
                              <p className="text-xl font-bold text-gray-900">
                                {(soum.montant_final || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $
                              </p>
                              <p className="text-xs text-gray-500 mt-1">{formatDepot(soum)}</p>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                              {/* Bouton Modifier */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditSoumission(soum)}
                                title="Modifier la soumission"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Modifier
                              </Button>
                              
                              {/* Bouton Refuser - toujours visible sauf si déjà refusée */}
                              {soum.statut !== 'refusee' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-500 text-red-500 hover:bg-red-50"
                                  onClick={() => setActionConfirm({ type: 'refuser', soumission: soum })}
                                  title="Marquer comme refusée (affecte les performances)"
                                  data-testid="btn-refuser"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Refusé
                                </Button>
                              )}

                              {/* Bouton Accepter manuellement - seulement si pas déjà acceptée */}
                              {!['acceptee', 'morte'].includes(soum.statut) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-green-600 text-green-600 hover:bg-green-50"
                                  onClick={() => setActionConfirm({ type: 'accepter', soumission: soum })}
                                  title="Accepter manuellement"
                                  data-testid="btn-accepter-manuel"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Accepter
                                </Button>
                              )}
                              
                              {/* Bouton Morte - toujours visible sauf si déjà morte */}
                              {soum.statut !== 'morte' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-gray-400 text-gray-500 hover:bg-gray-50"
                                  onClick={() => setActionConfirm({ type: 'morte', soumission: soum })}
                                  title="Marquer comme morte (n'affecte pas les statistiques)"
                                >
                                  <Archive className="w-4 h-4 mr-1" />
                                  Morte
                                </Button>
                              )}
                              
                              {/* Bouton Supprimer */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-600 text-red-600 hover:bg-red-50"
                                onClick={() => setDeleteConfirm(soum)}
                                title="Supprimer la soumission"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Créer/Modifier Soumission */}
      <CreerSoumissionComplete
        isOpen={showCreer || !!editSoumission}
        onClose={() => {
          setShowCreer(false);
          setEditSoumission(null);
        }}
        dossierId={dossier.id}
        soumissionToEdit={editSoumission}
        soumissionType={editSoumission?.type_soumission || soumissionType}
        onSuccess={() => {
          setShowCreer(false);
          setEditSoumission(null);
          onUpdate();
        }}
      />

      {/* Dialog Confirmation Suppression */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la soumission <strong>{deleteConfirm?.numero_soumission}</strong>?
              <br /><br />
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={() => handleDelete(deleteConfirm)}
            >
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmation Refuser/Morte/Accepter */}
      <Dialog open={!!actionConfirm} onOpenChange={() => setActionConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionConfirm?.type === 'refuser' ? 'Marquer comme refusée' : 
               actionConfirm?.type === 'accepter' ? 'Accepter manuellement' :
               actionConfirm?.type === 'accepter_final' ? 'Confirmer l\'acceptation manuelle' :
               'Marquer comme morte'}
            </DialogTitle>
            <DialogDescription>
              {actionConfirm?.type === 'refuser' ? (
                <>
                  Êtes-vous sûr de vouloir marquer la soumission <strong>{actionConfirm?.soumission?.numero_soumission}</strong> comme refusée?
                  <br /><br />
                  Cette action sera comptabilisée dans les statistiques de refus.
                </>
              ) : actionConfirm?.type === 'accepter' ? (
                <>
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-2">
                    <p className="font-semibold text-amber-800 mb-2">Avertissement important</p>
                    <p className="text-amber-700 text-sm">
                      En acceptant manuellement cette soumission <strong>sans signature électronique du client</strong>, 
                      votre entreprise s'expose à des <strong>risques légaux</strong> en cas de litige. 
                      La signature électronique constitue une preuve d'accord contractuel.
                    </p>
                  </div>
                  <br />
                  Voulez-vous tout de même marquer la soumission <strong>{actionConfirm?.soumission?.numero_soumission}</strong> comme acceptée?
                </>
              ) : actionConfirm?.type === 'accepter_final' ? (
                <>
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4 mt-2">
                    <p className="font-semibold text-red-800 mb-2">Dernière confirmation</p>
                    <p className="text-red-700 text-sm">
                      Vous confirmez accepter la soumission <strong>{actionConfirm?.soumission?.numero_soumission}</strong> sans 
                      signature du client. Cette décision est sous votre entière responsabilité.
                    </p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <label className="text-sm font-medium text-slate-700">Date de signature</label>
                    <input
                      data-testid="date-signature-input"
                      type="date"
                      value={signatureDate ? signatureDate.split('T')[0] : ''}
                      onChange={(e) => setSignatureDate(e.target.value ? e.target.value + 'T12:00:00' : '')}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder="Laisser vide pour aujourd'hui"
                    />
                    <p className="text-xs text-slate-500">Laisser vide pour utiliser la date d'aujourd'hui. Pour un contrat signé en 2025, entrez la vraie date.</p>
                  </div>
                </>
              ) : (
                <>
                  Êtes-vous sûr de vouloir marquer la soumission <strong>{actionConfirm?.soumission?.numero_soumission}</strong> comme morte?
                  <br /><br />
                  Une soumission "morte" n'affecte pas vos statistiques (ex: client ne répond plus, projet annulé, etc.)
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActionConfirm(null)}>Annuler</Button>
            <Button 
              className={
                actionConfirm?.type === 'refuser' ? 'bg-red-600 hover:bg-red-700' : 
                actionConfirm?.type === 'accepter' || actionConfirm?.type === 'accepter_final' ? 'bg-green-600 hover:bg-green-700' :
                'bg-gray-600 hover:bg-gray-700'
              }
              onClick={() => {
                if (actionConfirm?.type === 'refuser') {
                  handleRefuser(actionConfirm.soumission);
                } else if (actionConfirm?.type === 'accepter') {
                  // Étape 1 → Étape 2
                  setActionConfirm({ type: 'accepter_final', soumission: actionConfirm.soumission });
                } else if (actionConfirm?.type === 'accepter_final') {
                  // Étape 2 → Accepter
                  handleAccepter(actionConfirm.soumission);
                } else {
                  handleMorte(actionConfirm.soumission);
                }
              }}
            >
              {actionConfirm?.type === 'accepter' ? 'Oui, continuer' : 
               actionConfirm?.type === 'accepter_final' ? 'Je confirme l\'acceptation' : 
               'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
